<template>
<div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-2 ps-3 pb-2 mb-1 border-bottom">
	<div class="fs-4">
		Контрольно вимірювальні пункти
	</div>

	<!-- Burttons -->
	<div class="d-inline-block">
		<span
			v-if="accountStore.isPrimary"
			class="mx-1"
			tabindex="0"
			data-bs-toggle="tooltip"
			data-bs-title="історія налаштувань КВП"
		>
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary m-0 p-2"
				@click="$router.push({ name: 'posts_passport_history',})"
				style="border: 1; height: 2.4rem; aspect-ratio: 1 / 1;"
			>
				<button-icon icon="clock-history" color="currentColor" edge-size="20" />
			</button>
		</span>

		<span
			class="mx-1 me-4"
			tabindex="0"
			data-bs-toggle="tooltip"
			data-bs-title="створити КВП"
		>
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary"
				@click="isCreatePopupOpen=true"
				style="border: 1; height: 2.4rem; aspect-ratio: 1 / 1;"
				:disabled="!accountStore.permissions.includes('manage_posts')"
			>
				<button-icon icon="add-post" color="currentColor" edge-size="20" />
			</button>
		</span>
	</div>
</div>

<h4 v-if="isNoConnectionToServer">Немає св'язку з сервером</h4>

<easy-data-table
	class="data-table m-1 mt-0 me-1"
	table-class-name="customize-table"
	:headers="tableHeadersFiltered"
	:items="tableItems"
	:loading="isTableLoading"
	:filter-options="filterOptions"
	theme-color="rgb(198, 199, 200)"
	sort-by="name"
	sort-type="asc"
	:rows-items="[10,20,40,100]"
	:rows-per-page="easyDataTableRowsPerPage()"
	rows-per-page-message="записів на сторінці"
	rows-of-page-separator-message=" з "
	:table-min-height="380"
	:table-height="easyDataTableHeight()"
	:prevent-context-menu-row="false"
	alternating
	buttons-pagination
>
	<!-- If rows is empty -->
	<template #empty-message>
		КВП не знайдено
	</template>

	<template #loading>
		<div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
	</template>

	<!-- Filter to organization column -->
	<template #[orgNameHeaderSlotName]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="organizationCriterias.length > 0"
			:criterias="organizationCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'organization')"
			@checked-criterias="(criterias) => { saveFiters('organization', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to department column -->
	<template #[depertmentHeaderSlotName]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="departmentCriterias.length > 0"
			:criterias="departmentCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'department')"
			@checked-criterias="(criterias) => { saveFiters('department', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to sector column -->
	<template #[sectorHeaderSlotName]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="sectorCriterias.length > 0"
			:criterias="sectorCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'sector')"
			@checked-criterias="(criterias) => { saveFiters('sector', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to activity column -->
	<template #[activityHeaderSlotName]="header">
		<table-filter-checkboxes
			:criterias="activityCriterias"
			:default-checked="sessionStore.getFilter($route.name, 'activity')"
			@checked-criterias="(criterias) => { saveFiters('activity', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Time item customization -->
	<template #item-time="{ time }">
		<!-- {{ time === undefined ? "-" : timeToString(time) }} -->
		{{ timeFromNowPromt(time) }}
	</template>

	<!-- Name item customization -->
	<template #item-name="{ name, id }">
		<router-link
			class="text-start text-break text-reset text-decoration-none"
			:to="{ name: 'post_control', params: { id: `${id}` } }"
		>
			{{ name }}
		</router-link>
	</template>
</easy-data-table>

<create-post-popup
	v-if="isCreatePopupOpen"
	@created="isCreatePopupOpen = false; readAllPosts();"
	@close="isCreatePopupOpen = false;"
	@click.stop=""
/>
</template>




<script>
import CreatePostPopup from './CreatePostPopup.vue'
import TableFilterCheckboxes from '@/components/TableFilterCheckboxes.vue'
import ButtonIcon from '@/components/Icons/ButtonIcon.vue'
import { inject } from 'vue'
import { useAccontStore } from '@/stores/AccountStore.js'
import { usePostsStore } from '@/stores/PostsStore.js'
import { useSessionStore } from '@/stores/SessionStore'
import { useTooltip } from '@/composables/bootstrap-tooltip.js'
import { Post } from '@/models/post.js'
import { CustomNavChecklist, EmptyNavSection } from '../../customNavSection'
import {
	timeFromNowPromt,
	easyDataTableHeight,
	easyDataTableRowsPerPage,
	compareArrays,
} from '@/helpers.js'
import { EMPTY_COLUMN_VALUE, DEPARTMENT_TITLE, SECTOR_TITLE } from '@/app.conf'


export default {
	inject: [ 'headerHeight', ],
	
	components: {
		CreatePostPopup,
		TableFilterCheckboxes,
		ButtonIcon,
	},


	props: {
	},


	setup() {
		const accountStore = useAccontStore()
		const postsStore = usePostsStore()
		const sessionStore = useSessionStore()

		useTooltip()
		
		return {
			accountStore,
			postsStore,
			sessionStore,
			Post,
			timeFromNowPromt,
			easyDataTableHeight,
			easyDataTableRowsPerPage,
		}
	},


	data() {
		return {
			// Metrics update period
			metrics_update_rate: 5000,
			updateInterval: null,

			// Create new post
			isCreatePopupOpen: false,
			// State flags
			isNoConnectionToServer: false,

			// Table
			isTableLoading: true,

			// Filters
			filters: {
				organization: [],
				department: [],
				sector: [],
				activity: [],
			},

			// Columns for show in table
			columns: new CustomNavChecklist(),
		}
	},


	created() {
		this.readAllPosts()
	},


	mounted() {
		document.title = "Список КВП"

		this.initCustomNavbar()
	},


	methods: {
		async readAllPosts() {
			// Read all posts
			await this.postsStore.readPosts((result) => {
				this.isNoConnectionToServer = !result
				if (result) {
					this.postsStore.readMetrics((result) => {
						this.isNoConnectionToServer = !result
						this.isTableLoading = false
					})
					
					this.subscribeToUpdates()
				}
			})
		},

		initCustomNavbar() {
			const psk = Post.passportKeys
			const customColumns = []
			if (this.accountStore.isPrimary) {
				customColumns.push(psk.orgName)
			} 
			customColumns.push(psk.departmentId, psk.sectorId, psk.isActive)
			
			// Load custom nav from account if it present in storage
			let columnsInStorage = []
			for (const [, value] of Object.entries(this.accountStore.postsColumns)) {
				columnsInStorage.push(value.id)
			}
			
			let columnsActualy = [...customColumns]
			
			// If columns array not exist or empty
			if (this.accountStore.postsColumns == undefined
				|| this.accountStore.postsColumns.constructor !== Object
				|| !compareArrays(columnsInStorage, columnsActualy)
			) {
				// Create custom navigation data model with default state
				// Iterate table columns that can be hidden
				for (const col of customColumns) {
					let isShowColumn = true
					this.columns.addLine(
						col,
						this.colName(col),
						this.colName(col),
						isShowColumn,
						// event handler
						(/*state*/) => {
							this.toggleColumnHandler()
						}
					)
				}
			} else {
				// Parsing lines from account storage
				Object.values(this.accountStore.postsColumns).forEach((column) => {
					//console.log("Column = ", column)
					this.columns.addLine(
						column.id,
						this.colName(column.id),
						this.colName(column.id),
						column.isChecked,
						() => {
							this.toggleColumnHandler()
						}
					)
				})
			}

			// Publicate new custom navigation section for nav component
			const navCustom = inject('customNavSection')
			navCustom.value = this.columns
		},

		toggleColumnHandler() {
			this.accountStore.updatePostColumns(this.columns.linesArray)
			this.accountStore.save([this.accountStore.schema.settings.self,])
		},

		subscribeToUpdates() {
			// Clear interval if exist
			if (this.updateInterval !== null) {
				clearInterval(this.updateInterval)
				this.updateInterval = null
			}

			// Updates passport every metrics_update_rate seconds
			this.updateInterval = setInterval(async () => {
				this.postsStore.readMetrics((result) => {
					this.isNoConnectionToServer = !result
				})
			}, this.metrics_update_rate);
		},

		saveFiters(filterName, criterias) {
			this.filters[filterName] = criterias;
			const filters = this.sessionStore.getFilter(this.$route.name, filterName);
			filters.splice(0, filters.length);
			filters.push(...criterias);
		},

		// Column names
		colName (colId) {
			return this.columnNames.get(colId)
		},

		replaceEmptyColumnValue(value) {
			if (value === undefined || String(value) === EMPTY_COLUMN_VALUE) {
				return "-"
			} else {
				return value
			}
		},
	},


	computed: {
		columnNames() {
			return new Map([
				[Post.passportKeys.name,'Назва'],
				[Post.passportKeys.orgName,'Організація'],
				[Post.passportKeys.departmentId, 
					this.accountStore.organization[this.accountStore.schema.organization.departmentTitle] ?
					this.accountStore.organization[this.accountStore.schema.organization.departmentTitle] :
					DEPARTMENT_TITLE],
				[Post.passportKeys.sectorId,
					this.accountStore.organization[this.accountStore.schema.organization.sectorTitle] ?
					this.accountStore.organization[this.accountStore.schema.organization.sectorTitle] :
					SECTOR_TITLE],
				["ip", "IP КВП"],
				["time", "Опитування"],
				["imei", "IMEI"],
				["Eavg", "Eavg"],
				[Post.passportKeys.isActive, 'Активний'],
			])
		},

		tableHeaders() {
			let headers = []

			headers.push({ text: this.colName(Post.passportKeys.name), value: Post.passportKeys.name, sortable: true, width: 300, })
			if (this.accountStore.isPrimary) {
				headers.push({ text: this.colName(Post.passportKeys.orgName), value: Post.passportKeys.orgName, sortable: true, })
			}
			headers.push({ text: this.colName(Post.passportKeys.departmentId), value: Post.passportKeys.department, sortable: true, })
			headers.push({ text: this.colName(Post.passportKeys.sectorId), value: Post.passportKeys.sector, sortable: true, })
			headers.push({ text: this.colName(Post.passportKeys.ipAddress), value: Post.passportKeys.ipAddress })
			headers.push({ text: this.colName("time"), value: "time", sortable: true, width: 100, })
			headers.push({ text: this.colName("imei"), value: Post.metricsKeys.IMEI })
			headers.push({ text: this.colName("Eavg"), value: Post.metricsKeys.Eavg })
			headers.push({ text: this.colName(Post.passportKeys.isActive), value: Post.passportKeys.isActive })

			return headers
		},

		tableHeadersFiltered() {
			// Remavo from array hideable columns that not shown now
			const header = [...this.tableHeaders]
			this.columns.linesArray.forEach(column => {
				if (!column.isChecked) {
					const index = header.findIndex((el) => el.text == column.name)
					header.splice(index, 1)
				}
			})

			return header
		},

		tableItems() {
			const rows = []
			
			// Iterate over posts, make post row
			this.postsStore.postsArray.forEach(post => {
				let row = new Object()

				const name = post.shortName ? post.shortName : post.name
				if (post.passport.get(Post.passportKeys.isRegistered) === true) {
					row[Post.passportKeys.name] = name
				} else {
					row[Post.passportKeys.name] = "❗️" + name
				}
				row[Post.passportKeys.orgName] = post.passport.get(Post.passportKeys.orgName)
				row[Post.passportKeys.department] = this.replaceEmptyColumnValue(post.passport.get(Post.passportKeys.department)?.title)
				row[Post.passportKeys.sector] = this.replaceEmptyColumnValue(post.passport.get(Post.passportKeys.sector)?.title)
				row[Post.passportKeys.ipAddress] = post.passport.get(Post.passportKeys.ipAddress)
				row[Post.metricsKeys.IMEI] = post.getMetric(Post.metricsKeys.IMEI)?.value
				row[Post.metricsKeys.Eavg] = post.getMetric(Post.metricsKeys.Eavg)?.value
				row[Post.passportKeys.isActive] = post.isActive ? "так" : "ні"

				row = Object.assign(row, {
					"id": post.id,
					"time": post.getMetric(Post.metricsKeys.Eavg)?.time,
				})

				// Extra filter by default (show only active) if column is not showing.
				// If column is showing
				if (Object.values(this.accountStore.postsColumns).find(
					(el) => el.id == Post.passportKeys.isActive
					)?.isChecked
				) {
					// Add row to table
					rows.push(row)
				// If column is not showing
				} else {
					// If post is active, then show row. Otherwise - hide.
					if (post.isActive) {
						rows.push(row)
					}
				}
			})

			return rows
		},

		organizationCriterias() {
			return this.organizationNames.map((name) => name)
		},

		departmentCriterias() {
			return this.departmentNames.map((name) => name)
		},

		sectorCriterias() {
			return this.sectorNames.map((name) => name)
		},

		activityCriterias() {
			return ['так' , 'ні']
		},

		organizationNames() {
			let names = []

			this.postsStore.postsArray.forEach(post => {
				const name = post.passport.get(Post.passportKeys.orgName)
				if (!names.includes(name)) {
					names.push(name)
				}
			})

			return names
		},

		departmentNames() {
			let names = []

			this.postsStore.postsArray.forEach(post => {
				const department = post.passport.get(Post.passportKeys.department)
				let name = department === undefined ? "-" : department.title

				if (!names.includes(name)) {
					names.push(name)
				}
			})

			return names
		},

		sectorNames() {
			let names = []

			this.postsStore.postsArray.forEach(post => {
				const sector = post.passport.get(Post.passportKeys.sector)
				let name = sector === undefined ? "-" : sector.title
				
				if (!names.includes(name)) {
					names.push(name)
				}
			})

			return names
		},

		filterOptions() {
			const filterOptionsArray = new Array()
			const filteredHeaderFields = this.tableHeadersFiltered.map((el) => el.value)
			
			// Add filter only if column is shown
			if (filteredHeaderFields.includes(Post.passportKeys.orgName)) {
				filterOptionsArray.push({
					field: Post.passportKeys.orgName,
					comparison: 'in',
					criteria: this.filters.organization,
				})
			}

			// Add filter only if column is shown
			if (filteredHeaderFields.includes(Post.passportKeys.department)) {
				filterOptionsArray.push({
					field: Post.passportKeys.department,
					comparison: 'in',
					criteria: this.filters.department,
				})
			}

			// Add filter only if column is shown
			if (filteredHeaderFields.includes(Post.passportKeys.sector)) {
				filterOptionsArray.push({
					field: Post.passportKeys.sector,
					comparison: 'in',
					criteria: this.filters.sector,
				})
			}

			filterOptionsArray.push({
				field: Post.passportKeys.isActive,
				comparison: 'in',
				criteria: this.filters.activity,
			})

			return filterOptionsArray;
		},

		orgNameHeaderSlotName() { return "header-" + Post.passportKeys.orgName },
		depertmentHeaderSlotName() { return "header-" + Post.passportKeys.department },
		sectorHeaderSlotName() { return "header-" + Post.passportKeys.sector },
		activityHeaderSlotName() { return "header-" + Post.passportKeys.isActive },
		activityItemSlotName() { return "item-" + Post.passportKeys.isActive }
	},


	beforeUnmount() {
		// Clear custom nav section
		let navCustom = inject('customNavSection')
		navCustom.value = new EmptyNavSection()
		
		if (this.updateInterval != null) {
			clearInterval(this.updateInterval)
			this.updateInterval = null
		}
	},


	unmounted() {},
}
</script>




<style src="../../assets/widget.css" scoped></style>
<style src="@/assets/table.css" scoped></style>
<style scoped>
</style>